import EmptyState from '../../../components/EmptyState';
import { UserLinks } from '../../../components/UserLinks';
import { useFormatUtils } from '../../../hooks/useFormatUtils';
import type { FeedbackData } from '../../../types/feedback';
import { formatTimeAgo } from '../../../utils/timeUtils';

interface LatestFeedbackPanelProps {
  feedbackEntries: FeedbackData[];
}

export default function LatestFeedbackPanel({ feedbackEntries }: LatestFeedbackPanelProps) {
  const { formatDate } = useFormatUtils();

  if (feedbackEntries.length === 0) {
    return (
      <EmptyState
        title="No recent feedback"
        description="Once users submit feedback, it will appear here."
      />
    );
  }

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">Latest Feedback</h3>
      </div>
      <ul role="list" className="divide-y divide-gray-200">
        {feedbackEntries.map((feedback) => (
          <li key={feedback.id} className="px-4 sm:px-6 py-4">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 break-all">{feedback.userEmail || 'Anonymous user'}</p>
                <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-gray-500">
                  <span className="break-all">{feedback.userId || 'No user ID'}</span>
                  {feedback.userId && (
                    <div className="text-xs">
                      <UserLinks userId={feedback.userId} />
                    </div>
                  )}
                </div>
              </div>
              <p className="text-sm text-gray-500">{formatTimeAgo(feedback.createdAt ? new Date(formatDate(feedback.createdAt)) : undefined)}</p>
            </div>
            <p className="mt-3 text-sm text-gray-900 break-words">{feedback.text}</p>
            {feedback.source && (
              <span className="mt-3 inline-flex rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-gray-700">
                {feedback.source}
              </span>
            )}
            {feedback.flow && (
              <span className="mt-3 inline-flex rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-gray-700">
                {feedback.flow}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

